import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button, Dropdown, Modal, Skeleton, Spin, message } from "antd";
import {
  CloseOutlined,
  CloudUploadOutlined,
  DeleteOutlined,
  DownloadOutlined,
  EllipsisOutlined,
  FolderOutlined,
  FullscreenExitOutlined,
  FullscreenOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import ADAIcon from "../../../icons/ADAIcon";
import AssetApi from "../../../../api/AssetApi";
import {
  AssetDto,
  BulkUploadResultDto,
  FileUploadResult,
} from "../../../../generated/dashboard-client";
import { env } from "../../../../env/env";
import { ACCEPTED_INPUT_ACCEPT, isAcceptedFile } from "./assetUpload";
import {
  MoveCategoryModal,
  buildAssetMenuItems,
  uiCategoryFor,
  useAssetImageDims,
} from "./assetActionMenu";
import "./AssetLibraryModal.scss";

const CATEGORY_FILTERS = [
  { key: "ALL", label: "All", backend: ["LOGOS", "FONTS", "ICONS", "GRAPHICS", "PHOTOS"] },
  { key: "LOGOS", label: "Logos", backend: ["LOGOS"] },
  { key: "FONTS", label: "Fonts", backend: ["FONTS"] },
  // { key: "ICONS", label: "Icons", backend: ["ICONS"] },
  { key: "GRAPHICS", label: "Graphics", backend: ["GRAPHICS", "PHOTOS"] },
];

const UI_CATEGORY_LABELS: Record<string, string> = {
  LOGOS: "Logos",
  FONTS: "Fonts",
  ICONS: "Icons",
  GRAPHICS: "Graphics",
  PHOTOS: "Graphics",
};

const MAX_UPLOAD_BYTES = 10 * 1024 * 1024;

type SortKey = "name" | "category" | "fileSize";
type SortDir = "asc" | "desc";

function formatFileSize(bytes?: number): string {
  if (!bytes && bytes !== 0) return "";
  if (bytes < 1024) return bytes + " B";
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " KB";
  return (bytes / (1024 * 1024)).toFixed(1) + " MB";
}

// Legacy font filenames carry a `-<uniqueId>` suffix from the upload pipeline
// (e.g. `playfair-display-400-uimnbobs0uqb.ttf`). Strip it for display only —
// the underlying asset name is unchanged.
function displayName(asset: AssetDto): string {
  const name = asset.name || "";
  if (asset.category !== "FONTS") return name;
  return name.replace(/-[a-zA-Z0-9]{8,}(\.[^.]+)?$/, "$1");
}

interface Props {
  open: boolean;
  teamId: string;
  brandKitId: string;
  onClose: () => void;
  onAssetsAdded?: (count: number) => void;
}

export function AssetLibraryModal({
  open,
  teamId,
  brandKitId,
  onClose,
  onAssetsAdded,
}: Props) {
  const [assets, setAssets] = useState<AssetDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [activeCategory, setActiveCategory] = useState<string>("ALL");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [dragging, setDragging] = useState(false);
  const [uploading, setUploading] = useState(0);
  const [fullscreen, setFullscreen] = useState(false);
  const [adding, setAdding] = useState(false);
  const [bulkDownloading, setBulkDownloading] = useState(false);
  const [moveModalOpen, setMoveModalOpen] = useState(false);
  const [moveModalIds, setMoveModalIds] = useState<string[]>([]);
  const [sortKey, setSortKey] = useState<SortKey>("name");
  const [sortDir, setSortDir] = useState<SortDir>("asc");

  const [altTextOpen, setAltTextOpen] = useState(false);
  const [altTextValue, setAltTextValue] = useState("");
  const [altTextSaving, setAltTextSaving] = useState(false);
  const [altTextAsset, setAltTextAsset] = useState<AssetDto | null>(null);

  const [deleteAsset, setDeleteAsset] = useState<AssetDto | null>(null);
  const [deletingOne, setDeletingOne] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);

  const load = useCallback(() => {
    if (!teamId) return;
    setLoading(true);
    AssetApi.list(teamId)
      .then((resp) => setAssets(resp.data || []))
      .catch(() => message.error("Failed to load assets"))
      .finally(() => setLoading(false));
  }, [teamId]);

  useEffect(() => {
    if (open) {
      setSelectedIds([]);
      setSearchQuery("");
      setActiveCategory("ALL");
      load();
    }
  }, [open, load]);

  const counts = useMemo(() => {
    const result: Record<string, number> = { ALL: 0, LOGOS: 0, FONTS: 0, ICONS: 0, GRAPHICS: 0 };
    assets.forEach((a) => {
      if (a.category === "LOGOS") result.LOGOS += 1;
      else if (a.category === "FONTS") result.FONTS += 1;
      else if (a.category === "ICONS") result.ICONS += 1;
      else if (a.category === "GRAPHICS" || a.category === "PHOTOS") result.GRAPHICS += 1;
      if (["LOGOS", "FONTS", "ICONS", "GRAPHICS", "PHOTOS"].includes(a.category || "")) {
        result.ALL += 1;
      }
    });
    return result;
  }, [assets]);

  const filtered = useMemo(() => {
    const filter = CATEGORY_FILTERS.find((c) => c.key === activeCategory);
    const allowed = filter?.backend || [];
    const q = searchQuery.trim().toLowerCase();
    let list = assets.filter((a) => allowed.includes(a.category || ""));
    if (q) list = list.filter((a) => (a.name || "").toLowerCase().includes(q));
    const mul = sortDir === "asc" ? 1 : -1;
    list = [...list].sort((a, b) => {
      if (sortKey === "fileSize") return ((a.fileSize || 0) - (b.fileSize || 0)) * mul;
      if (sortKey === "category") {
        const la = UI_CATEGORY_LABELS[a.category || ""] || "";
        const lb = UI_CATEGORY_LABELS[b.category || ""] || "";
        return la.localeCompare(lb) * mul;
      }
      return (a.name || "").localeCompare(b.name || "") * mul;
    });
    return list;
  }, [assets, activeCategory, searchQuery, sortKey, sortDir]);

  function toggleSort(key: SortKey) {
    if (sortKey === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir("asc");
    }
  }

  const allVisibleSelected =
    filtered.length > 0 && filtered.every((a) => selectedIds.includes(a.id!));

  function toggleSelectAll() {
    if (allVisibleSelected) {
      const visibleIds = new Set(filtered.map((a) => a.id!));
      setSelectedIds((prev) => prev.filter((id) => !visibleIds.has(id)));
    } else {
      setSelectedIds((prev) => {
        const merged = new Set(prev);
        filtered.forEach((a) => merged.add(a.id!));
        return Array.from(merged);
      });
    }
  }

  function toggleSelect(id: string) {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  }

  function uploadFiles(files: File[]) {
    if (!teamId || files.length === 0) return;
    setUploading((n) => n + files.length);
    const category = activeCategory !== "ALL" ? activeCategory : undefined;
    AssetApi.bulkUpload(teamId, files, category)
      .then((resp) => {
        const result: BulkUploadResultDto = resp.data;
        const results = result.results || [];
        const uploaded = results
          .filter((r: FileUploadResult) => r.success && r.asset)
          .map((r: FileUploadResult) => r.asset!);
        if (uploaded.length > 0) {
          setAssets((prev) => [...uploaded, ...prev]);
        }
        if (result.successCount && result.successCount > 0) {
          message.success(
            `${result.successCount} file${result.successCount > 1 ? "s" : ""} uploaded`
          );
        }
        results
          .filter((r: FileUploadResult) => !r.success)
          .forEach((r: FileUploadResult) =>
            message.error(`Failed to upload "${r.fileName}": ${r.error}`)
          );
      })
      .catch(() => message.error("Upload failed"))
      .finally(() => setUploading((n) => n - files.length));
  }

  function onFilesSelected(files: FileList | null) {
    if (!files) return;
    const all = Array.from(files);
    const unsupported = all.filter((f) => !isAcceptedFile(f));
    const supported = all.filter((f) => isAcceptedFile(f));
    if (unsupported.length) {
      message.error(
        unsupported.length === 1
          ? `"${unsupported[0].name}" is not a supported file type`
          : `${unsupported.length} files are not a supported file type`
      );
    }
    const accepted = supported.filter((f) => f.size <= MAX_UPLOAD_BYTES);
    const tooLarge = supported.filter((f) => f.size > MAX_UPLOAD_BYTES);
    if (tooLarge.length) {
      message.error(
        tooLarge.length === 1
          ? `"${tooLarge[0].name}" exceeds the 10MB upload limit`
          : `${tooLarge.length} files exceed the 10MB upload limit`
      );
    }
    if (accepted.length) uploadFiles(accepted);
  }

  function handleDrop(e: React.DragEvent) {
    e.preventDefault();
    setDragging(false);
    onFilesSelected(e.dataTransfer.files);
  }

  function handleBulkDelete() {
    if (selectedIds.length === 0) return;
    const count = selectedIds.length;
    const noun = `file${count > 1 ? "s" : ""}`;
    Modal.confirm({
      title: "Delete permanently?",
      content: `${count} ${noun} will be permanently deleted from the Asset Library and removed from every Brand Kit ${count > 1 ? "they belong" : "it belongs"} to. This cannot be undone.`,
      okText: "Delete permanently",
      okButtonProps: { danger: true },
      onOk: () =>
        AssetApi.bulkDelete(selectedIds)
          .then(() => {
            const removed = new Set(selectedIds);
            setAssets((prev) => prev.filter((a) => !removed.has(a.id!)));
            setSelectedIds([]);
            message.success(
              `${count} ${noun} deleted`
            );
          })
          .catch(() => message.error("Failed to delete assets")),
    });
  }

  function handleBulkDownload() {
    if (selectedIds.length === 0 || bulkDownloading) return;
    setBulkDownloading(true);
    AssetApi.bulkDownload(selectedIds)
      .then((resp) => {
        const blob = new Blob([resp.data], { type: "application/zip" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = "assets.zip";
        a.click();
        URL.revokeObjectURL(url);
      })
      .catch(() => message.error("Failed to download assets"))
      .finally(() => setBulkDownloading(false));
  }

  function openMoveModal(ids: string[]) {
    if (ids.length === 0) return;
    setMoveModalIds(ids);
    setMoveModalOpen(true);
  }

  function closeMoveModal() {
    setMoveModalOpen(false);
    setMoveModalIds([]);
  }

  function handleMovedFromModal(updated: AssetDto[]) {
    const updatedById = new Map(updated.map((a) => [a.id!, a]));
    setAssets((prev) =>
      prev.map((a) => (updatedById.has(a.id!) ? updatedById.get(a.id!)! : a))
    );
    setSelectedIds((prev) => prev.filter((id) => !updatedById.has(id)));
    closeMoveModal();
  }

  function handleAddToBrandKit() {
    if (selectedIds.length === 0 || !brandKitId) return;
    setAdding(true);
    const count = selectedIds.length;
    AssetApi.assignToBrand(selectedIds, brandKitId)
      .then(() => {
        message.success(
          `${count} asset${count > 1 ? "s" : ""} added to brand kit`
        );
        setSelectedIds([]);
        onAssetsAdded?.(count);
        onClose();
      })
      .catch(() => message.error("Failed to add assets to brand kit"))
      .finally(() => setAdding(false));
  }

  function handleDownload(asset: AssetDto) {
    const url = `${env.s3Path}/${asset.path}`;
    const a = document.createElement("a");
    a.href = url;
    a.download = asset.name || "";
    a.target = "_blank";
    a.click();
  }

  function handleDeleteOne(asset: AssetDto) {
    setDeleteAsset(asset);
  }

  function closeDeleteModal() {
    if (deletingOne) return;
    setDeleteAsset(null);
  }

  function confirmDeleteOne() {
    if (!deleteAsset) return;
    const id = deleteAsset.id!;
    setDeletingOne(true);
    AssetApi.delete(id)
      .then(() => {
        setAssets((prev) => prev.filter((a) => a.id !== id));
        setSelectedIds((prev) => prev.filter((x) => x !== id));
        message.success("Asset deleted");
        setDeleteAsset(null);
      })
      .catch(() => message.error("Failed to delete asset"))
      .finally(() => setDeletingOne(false));
  }

  function handleAltText(asset: AssetDto) {
    setAltTextAsset(asset);
    setAltTextValue(asset.altText || "");
    setAltTextOpen(true);
  }

  function saveAltText() {
    if (!altTextAsset) return;
    setAltTextSaving(true);
    AssetApi.updateAltText(altTextAsset.id!, altTextValue)
      .then((resp) => {
        setAssets((prev) => prev.map((a) => (a.id === resp.data.id ? resp.data : a)));
        setAltTextOpen(false);
      })
      .catch(() => message.error("Failed to update ADA text"))
      .finally(() => setAltTextSaving(false));
  }

  const imgDims = useAssetImageDims(assets);

  const moveExcludeCategory = useMemo(() => {
    if (moveModalIds.length === 0) return undefined;
    const idSet = new Set(moveModalIds);
    const targetUiCats = new Set(
      assets.filter((a) => idSet.has(a.id!)).map((a) => uiCategoryFor(a.category))
    );
    if (targetUiCats.size === 1) {
      return targetUiCats.values().next().value as string;
    }
    return undefined;
  }, [moveModalIds, assets]);

  function rowMenu(asset: AssetDto) {
    return {
      items: buildAssetMenuItems({
        asset,
        dims: asset.id ? imgDims[asset.id] : undefined,
        onAda: handleAltText,
        onDownload: handleDownload,
        onMove: (a) => openMoveModal([a.id!]),
        onRemove: handleDeleteOne,
        removeLabel: "Delete",
        removeIcon: <DeleteOutlined />,
      }),
    };
  }

  function sortIcon(key: SortKey) {
    const active = sortKey === key;
    return (
      <span className={`sort-icon${active ? " active" : ""}${active && sortDir === "desc" ? " desc" : ""}`}>
        <span className="sort-up" />
        <span className="sort-down" />
      </span>
    );
  }

  function renderThumb(asset: AssetDto) {
    const isImage = asset.fileType?.startsWith("image/");
    if (isImage) {
      return (
        <div
          className="al-thumb"
          style={{ backgroundImage: `url("${env.s3Path}/${asset.path}")` }}
        />
      );
    }
    const isFont = asset.category === "FONTS";
    return (
      <div className={`al-thumb al-thumb-file${isFont ? " al-thumb-font" : ""}`}>
        {isFont ? "Aa" : (asset.name || "").split(".").pop()?.toUpperCase()}
      </div>
    );
  }

  const subtitle = `${counts.ALL} file${counts.ALL === 1 ? "" : "s"} · shared storage across all Brand Kits`;

  return (
    <Modal
      className={`asset-library-modal${fullscreen ? " fullscreen" : ""}`}
      open={open}
      onCancel={onClose}
      footer={null}
      closable={false}
      width={fullscreen ? "100vw" : 1200}
      centered={!fullscreen}
      maskClosable={false}
      destroyOnClose
    >
      <div className="al-header">
        <div className="al-header-center">
          <h2>Asset Library</h2>
          <p>{subtitle}</p>
        </div>
        <div className="al-header-actions">
          <button
            type="button"
            className="al-icon-btn"
            onClick={() => setFullscreen((f) => !f)}
            aria-label="Toggle fullscreen"
          >
            {fullscreen ? <FullscreenExitOutlined /> : <FullscreenOutlined />}
          </button>
          <button type="button" className="al-icon-btn" onClick={onClose} aria-label="Close">
            <CloseOutlined />
          </button>
        </div>
      </div>

      <div className="al-body">
        <aside className="al-sidebar">
          <Spin spinning={uploading > 0} tip="Uploading...">
            <div
              className={`al-upload${dragging ? " dragging" : ""}`}
              onDragOver={(e) => {
                e.preventDefault();
                setDragging(true);
              }}
              onDragLeave={() => setDragging(false)}
              onDrop={handleDrop}
            >
              <CloudUploadOutlined className="al-upload-icon" />
              <p>
                Drag and drop your logos, fonts, icons, and graphics here
                <br />
                or{" "}
                <a onClick={() => fileInputRef.current?.click()}>select file from your computer</a>
              </p>
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept={ACCEPTED_INPUT_ACCEPT}
                style={{ display: "none" }}
                onChange={(e) => {
                  onFilesSelected(e.target.files);
                  e.target.value = "";
                }}
              />
            </div>
          </Spin>

          <ul className="al-category-list">
            {CATEGORY_FILTERS.map((c) => (
              <li
                key={c.key}
                className={`al-category-item${activeCategory === c.key ? " active" : ""}`}
                onClick={() => setActiveCategory(c.key)}
              >
                <span className="al-category-label">{c.label}</span>
                <span className="al-category-count">{counts[c.key] ?? 0}</span>
              </li>
            ))}
          </ul>
        </aside>

        <section className="al-main">
          <div className="al-search-wrap">
            <span className="al-search-icon">
              <SearchOutlined />
            </span>
            <input
              className="al-search"
              placeholder="Search assets"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>

          <div className="al-table">
            <div className="al-thead">
              <div className="al-th al-col-check">
                <input
                  type="checkbox"
                  checked={allVisibleSelected}
                  onChange={toggleSelectAll}
                />
              </div>
              <div className="al-th al-col-name" onClick={() => toggleSort("name")}>
                <span>Name</span>
                {sortIcon("name")}
              </div>
              <div className="al-th al-col-category" onClick={() => toggleSort("category")}>
                <span>Category</span>
                {sortIcon("category")}
              </div>
              <div className="al-th al-col-size" onClick={() => toggleSort("fileSize")}>
                <span>Size</span>
                {sortIcon("fileSize")}
              </div>
              <div className="al-th al-col-actions" />
            </div>

            <div className="al-tbody">
              {loading ? (
                <div className="al-loading">
                  <Skeleton active />
                </div>
              ) : filtered.length === 0 ? (
                <div className="al-empty">No assets found</div>
              ) : (
                filtered.map((asset) => {
                  const checked = selectedIds.includes(asset.id!);
                  return (
                    <div
                      key={asset.id}
                      className={`al-row${checked ? " selected" : ""}`}
                    >
                      <div className="al-td al-col-check">
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggleSelect(asset.id!)}
                        />
                      </div>
                      <div className="al-td al-col-name">
                        {renderThumb(asset)}
                        <span className="al-name-text" title={displayName(asset)}>
                          {displayName(asset)}
                        </span>
                      </div>
                      <div className="al-td al-col-category">
                        {UI_CATEGORY_LABELS[asset.category || ""] || asset.category || ""}
                      </div>
                      <div className="al-td al-col-size">{formatFileSize(asset.fileSize)}</div>
                      <div className="al-td al-col-actions">
                        <Dropdown
                          menu={rowMenu(asset)}
                          trigger={["click"]}
                          overlayClassName="asset-action-menu"
                        >
                          <button type="button" className="al-row-menu-btn" aria-label="Actions">
                            <EllipsisOutlined />
                          </button>
                        </Dropdown>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </section>
      </div>

      <div className={`al-bulk-bar${selectedIds.length > 0 ? " visible" : ""}`}>
        <Button
          type="text"
          className="al-bulk-close-btn"
          icon={<CloseOutlined />}
          onClick={() => setSelectedIds([])}
          title="Clear selection"
        />
        <h4>
          {selectedIds.length} item{selectedIds.length === 1 ? "" : "s"} selected
        </h4>
        <div className="al-bulk-actions">
          <Button
            type="primary"
            className="al-bulk-add-btn"
            loading={adding}
            onClick={handleAddToBrandKit}
          >
            Add to Brand Kit
          </Button>
          <Button
            type="text"
            icon={<DownloadOutlined />}
            onClick={handleBulkDownload}
            loading={bulkDownloading}
            title="Download"
          />
          <Button
            type="text"
            icon={<FolderOutlined />}
            onClick={() => openMoveModal(selectedIds)}
            title="Move to Category"
          />
          <Button
            type="text"
            className="al-bulk-delete-btn"
            icon={<DeleteOutlined />}
            onClick={handleBulkDelete}
            title="Delete"
          />
        </div>
      </div>

      <Modal
        className="asset-alt-text-modal one"
        open={altTextOpen}
        onCancel={() => setAltTextOpen(false)}
        footer={null}
        width={500}
      >
        {altTextAsset && altTextAsset.fileType?.startsWith("image/") && (
          <div className="alt-text-preview">
            <img src={`${env.s3Path}/${altTextAsset.path}`} alt={altTextAsset.name} />
          </div>
        )}
        <h3>
          <ADAIcon /> Accessibility Text
        </h3>
        <p>Image description necessary for ADA compliance and screen readers</p>
        <textarea
          placeholder="Description 125 chars max"
          maxLength={125}
          value={altTextValue}
          onChange={(e) => setAltTextValue(e.target.value)}
        />
        <footer>
          <Button
            type="primary"
            disabled={!altTextValue}
            loading={altTextSaving}
            onClick={saveAltText}
          >
            Done
          </Button>
        </footer>
      </Modal>

      <MoveCategoryModal
        open={moveModalOpen}
        assetIds={moveModalIds}
        excludeCategory={moveExcludeCategory}
        onCancel={closeMoveModal}
        onMoved={handleMovedFromModal}
      />

      <Modal
        className={"clean one"}
        open={!!deleteAsset}
        onCancel={closeDeleteModal}
        footer={null}
      >
        <header>
          <h2>Are you sure?</h2>
        </header>

        <p>
          This asset will be permanently deleted from the Asset Library and removed from every Brand Kit it belongs to. This action cannot be undone.
        </p>

        <footer>
          <Button size="large" onClick={closeDeleteModal} disabled={deletingOne}>
            Cancel
          </Button>
          <Button
            size="large"
            className="primary"
            onClick={confirmDeleteOne}
            loading={deletingOne}
          >
            Delete
          </Button>
        </footer>
      </Modal>
    </Modal>
  );
}
